import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { StyleSheet, Platform, Dimensions, TextInput, ScrollView, Pressable } from 'react-native';
import * as Clipboard from 'expo-clipboard';
import * as FileSystem from 'expo-file-system';
import styles from '@stylesheet';
import { Text, View } from '@constants/Themed';
import SvgQRCode from 'react-native-qrcode-svg';
import { AntDesign, MaterialIcons, Feather } from '@expo/vector-icons';
import { Button, XStack, YStack, H4, H6, Separator, Card, SizableText } from 'tamagui';
import * as Sharing from 'expo-sharing';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { Dialog } from 'tamagui';

interface MeetingShareModalProps {
  baseUrl?: string;
}

export interface MeetingShareModalHandle {
  openModal: (meetingData: {
    roomId: string;
    meetingName: string;
    isHost: boolean;
    password?: string;
  }) => void;
}

const MeetingShareModal = forwardRef<MeetingShareModalHandle, MeetingShareModalProps>((props, ref) => {
  const { baseUrl = process.env.EXPO_PUBLIC_WEB_API } = props;
  const [meetingData, setMeetingData] = useState<{
    roomId: string;
    meetingName: string;
    isHost: boolean;
    password?: string;
  }>({
    roomId: '',
    meetingName: '',
    isHost: false,
    password: ''
  });

  const qrCodeRef = useRef<any>(null);
  const [meetingLink, setMeetingLink] = useState('');
  const [copyStatus, setCopyStatus] = useState('');
  const [downloadStatus, setDownloadStatus] = useState('');
  const [embedCode, setEmbedCode] = useState('');
  const [embedWidth, setEmbedWidth] = useState('100%');
  const [embedHeight, setEmbedHeight] = useState('600');
  const [showEmbedOptions, setShowEmbedOptions] = useState(false);
  const [embedCopyStatus, setEmbedCopyStatus] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const theme = themeSelector();
  const { width } = Dimensions.get('window');
  const isTablet = width > 768;

  useImperativeHandle(ref, () => ({
    openModal: (data) => {
      console.log('MeetingShareModal openModal called with data:', data);
      setMeetingData(data);

      // Generate meeting link - use frontend route
      const link = `${baseUrl}/en/meeting/${data.roomId}`;
      setMeetingLink(link);
      generateEmbedCode(link, embedWidth, embedHeight);

      // Open the custom modal
      setIsVisible(true);
    }
  }));

  // Debug: Log when component mounts
  useEffect(() => {
    console.log('MeetingShareModal component mounted');
    return () => {
      console.log('MeetingShareModal component unmounted');
    };
  }, []);

  const generateEmbedCode = (linkUrl: string, width: string, height: string) => {
    const code = `<iframe 
  src="${linkUrl}" 
  width="${width}" 
  height="${height}px" 
  frameborder="0" 
  scrolling="auto"
  style="border: none; border-radius: 8px; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
</iframe>`;
    setEmbedCode(code);
  };

  const updateEmbedDimensions = (width: string, height: string) => {
    setEmbedWidth(width);
    setEmbedHeight(height);
    generateEmbedCode(meetingLink, width, height);
  };

  const copyEmbedCode = async () => {
    try {
      await Clipboard.setStringAsync(embedCode);
      setEmbedCopyStatus('Embed code copied to clipboard!');
      setTimeout(() => setEmbedCopyStatus(''), 3000);
    } catch (error) {
      setEmbedCopyStatus('Failed to copy embed code');
      setTimeout(() => setEmbedCopyStatus(''), 3000);
    }
  };

  const copyToClipboard = async () => {
    try {
      await Clipboard.setStringAsync(meetingLink);
      setCopyStatus('Meeting link copied to clipboard!');
      setTimeout(() => setCopyStatus(''), 3000);
    } catch (error) {
      setCopyStatus('Failed to copy link');
      setTimeout(() => setCopyStatus(''), 3000);
    }
  };

  const shareLink = async () => {
    try {
      if (await Sharing.isAvailableAsync()) {
        await Sharing.shareAsync(meetingLink, {
          dialogTitle: `Share ${meetingData.meetingName} Meeting`
        });
      } else {
        copyToClipboard();
      }
    } catch (error) {
      copyToClipboard();
    }
  };

  const downloadQRCode = async () => {
    try {
      setDownloadStatus('Generating QR code...');

      if (Platform.OS === 'web') {
        // Web download using canvas
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const size = 512; // High quality size

        canvas.width = size;
        canvas.height = size;

        // Create QR code SVG
        const QRCode = require('qrcode');
        const qrDataURL = await QRCode.toDataURL(meetingLink, {
          width: size,
          margin: 2,
          color: {
            dark: '#333333',
            light: '#FFFFFF'
          }
        });

        // Create download link
        const downloadLink = document.createElement('a');
        downloadLink.href = qrDataURL;
        downloadLink.download = `${meetingData.meetingName}_Meeting_${Date.now()}.png`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);

        setDownloadStatus('QR code downloaded successfully!');
      } else {
        // Mobile download using react-native-qrcode-svg
        if (qrCodeRef.current) {
          qrCodeRef.current.toDataURL((dataURL: string) => {
            const filename = `${meetingData.meetingName}_Meeting_${Date.now()}.png`;

            if (Platform.OS === 'ios' || Platform.OS === 'android') {
              // For mobile, we'll use the share functionality as a fallback
              const base64Data = dataURL.replace('data:image/png;base64,', '');

              if (Sharing.isAvailableAsync()) {
                // Create a temporary file
                const fileUri = `${(FileSystem.Paths as any).documentDirectory}${filename}`;
                FileSystem.writeAsStringAsync(fileUri, base64Data, {
                  encoding: 'base64' as any,
                }).then(() => {
                  Sharing.shareAsync(fileUri, {
                    dialogTitle: 'Save Meeting QR Code',
                    mimeType: 'image/png'
                  });
                  setDownloadStatus('QR code shared successfully!');
                }).catch(() => {
                  setDownloadStatus('Failed to save QR code');
                });
              }
            }
          });
        }
      }
    } catch (error) {
      console.error('Download error:', error);
      setDownloadStatus('Failed to download QR code');
    }

    setTimeout(() => setDownloadStatus(''), 3000);
  };

  useEffect(() => {
    if (meetingLink) {
      generateEmbedCode(meetingLink, embedWidth, embedHeight);
    }
  }, [meetingLink, embedWidth, embedHeight]);

  return (
    <Dialog open={isVisible} onOpenChange={setIsVisible}>
      <Dialog.Portal>
        <Dialog.Overlay
          animation="quick"
          opacity={0.6}
          style={{
            zIndex: 1000,
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            ...(Platform.OS === 'web' && {
              backdropFilter: 'blur(8px)',
            })
          }}
          enterStyle={{ opacity: 0 }}
          exitStyle={{ opacity: 0 }}
        />
        <Dialog.Content
          bordered
          elevate
          animation={[
            'quick',
            {
              opacity: {
                overshootClamping: true,
              },
            },
          ]}
          enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
          exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
          x={0}
          scale={1}
          style={[
            {
              padding: 0,
              minWidth: 500,
              maxWidth: 600,
              maxHeight: "85%",
              borderRadius: 24,
              overflow: 'hidden',
              elevation: 12,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 12 },
              shadowOpacity: 0.15,
              shadowRadius: 32,
              backgroundColor: theme ? 'rgba(255, 255, 255, 0.98)' : 'rgba(30, 41, 59, 0.98)',
              borderWidth: 1,
              borderColor: theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)',
            },
            Platform.OS === 'web' && {
              backdropFilter: 'blur(24px)',
              boxShadow: '0 12px 48px rgba(0,0,0,0.12)',
            }
          ]}
          opacity={1}
          y={0}
        >
          {/* Header */}
          <View style={{
            backgroundColor: theme ? '#f8fafc' : '#374151',
            paddingHorizontal: 24,
            paddingVertical: 20,
            borderBottomWidth: 1,
            borderBottomColor: theme ? '#e5e7eb' : '#4b5563',
          }}>
            <XStack alignItems="center" gap="$3">
              <View style={{
                backgroundColor: theme ? "#f0f9ff" : "#0c4a6e",
                borderRadius: 12,
                padding: 10,
              }}>
                <MaterialIcons
                  name="share"
                  size={24}
                  color={theme ? "#0ea5e9" : "#38bdf8"}
                />
              </View>
              <YStack flex={1}>
                <Dialog.Title style={{
                  fontSize: 20,
                  fontWeight: '700',
                  color: theme ? '#1e293b' : '#f1f5f9',
                  marginBottom: 4,
                  margin: 0,
                }}>
                  Share Meeting
                </Dialog.Title>
                <Dialog.Description style={{
                  fontSize: 14,
                  color: theme ? '#64748b' : '#94a3b8',
                  fontWeight: '500',
                  margin: 0,
                }}>
                  Share this meeting link with participants
                </Dialog.Description>
              </YStack>
            </XStack>
          </View>

          {/* Content */}
          <ScrollView style={{ maxHeight: 500 }}>
            <View style={{ padding: 24 }}>
              <YStack gap="$4">
                {/* Meeting Info */}
                <View style={{
                  backgroundColor: theme ? '#f8fafc' : '#374151',
                  borderRadius: 16,
                  padding: 20,
                  borderWidth: 1,
                  borderColor: theme ? '#e5e7eb' : '#4b5563',
                }}>
                  <XStack alignItems="center" gap="$3" marginBottom="$3">
                    <View style={{
                      backgroundColor: theme ? "#fef3c7" : "#451a03",
                      borderRadius: 10,
                      padding: 8,
                    }}>
                      <MaterialIcons
                        name="meeting-room"
                        size={24}
                        color={theme ? "#f59e0b" : "#fbbf24"}
                      />
                    </View>
                    <YStack flex={1}>
                      <Text style={{
                        fontSize: 16,
                        fontWeight: '600',
                        color: theme ? '#1e293b' : '#f1f5f9',
                        marginBottom: 2,
                      }}>
                        {meetingData.meetingName}
                      </Text>
                      <Text style={{
                        fontSize: 12,
                        color: theme ? '#64748b' : '#94a3b8',
                      }}>
                        Room ID: {meetingData.roomId}
                      </Text>
                    </YStack>
                  </XStack>
                </View>

                {/* QR Code Section */}
                <View style={{
                  backgroundColor: theme ? '#f8fafc' : '#374151',
                  borderRadius: 16,
                  padding: 20,
                  borderWidth: 1,
                  borderColor: theme ? '#e5e7eb' : '#4b5563',
                  alignItems: 'center'
                }}>
                  <Text style={{
                    fontSize: 16,
                    fontWeight: '600',
                    color: theme ? '#1e293b' : '#f1f5f9',
                    marginBottom: 16,
                    textAlign: 'center'
                  }}>
                    Scan QR Code to Join
                  </Text>

                  <View style={{
                    backgroundColor: 'white',
                    padding: 16,
                    borderRadius: 12,
                    marginBottom: 16,
                    shadowColor: '#000',
                    shadowOffset: { width: 0, height: 2 },
                    shadowOpacity: 0.1,
                    shadowRadius: 8,
                    elevation: 4,
                  }}>
                    <SvgQRCode
                      ref={qrCodeRef}
                      size={200}
                      value={meetingLink}
                      backgroundColor="white"
                      color="#333"
                      getRef={(c) => (qrCodeRef.current = c)}
                    />
                  </View>

                  <Text style={{
                    fontSize: 12,
                    color: theme ? '#64748b' : '#94a3b8',
                    textAlign: 'center',
                    fontFamily: Platform.OS === 'web' ? 'monospace' : 'System',
                    marginBottom: 16
                  }}>
                    {meetingLink.length > 50 ? `${meetingLink.substring(0, 50)}...` : meetingLink}
                  </Text>
                </View>

                {/* Status Messages */}
                {copyStatus ? (
                  <View style={[stylesLocal.statusContainer, {
                    backgroundColor: copyStatus.includes('Failed') ? '#ffe6e6' : '#e8f5e8'
                  }]}>
                    <Feather
                      name={copyStatus.includes('Failed') ? "alert-circle" : "check-circle"}
                      size={20}
                      color={copyStatus.includes('Failed') ? '#e74c3c' : '#27ae60'}
                    />
                    <Text style={{
                      color: copyStatus.includes('Failed') ? '#e74c3c' : '#27ae60',
                      fontSize: 14,
                      fontWeight: '500',
                      marginLeft: 8
                    }}>
                      {copyStatus}
                    </Text>
                  </View>
                ) : null}

                {downloadStatus ? (
                  <View style={[stylesLocal.statusContainer, {
                    backgroundColor: downloadStatus.includes('Failed') ? '#ffe6e6' : downloadStatus.includes('Generating') ? '#fff3cd' : '#e8f5e8'
                  }]}>
                    <Feather
                      name={downloadStatus.includes('Failed') ? "alert-circle" : downloadStatus.includes('Generating') ? "download-cloud" : "check-circle"}
                      size={20}
                      color={downloadStatus.includes('Failed') ? '#e74c3c' : downloadStatus.includes('Generating') ? '#856404' : '#27ae60'}
                    />
                    <Text style={{
                      color: downloadStatus.includes('Failed') ? '#e74c3c' : downloadStatus.includes('Generating') ? '#856404' : '#27ae60',
                      fontSize: 14,
                      fontWeight: '500',
                      marginLeft: 8
                    }}>
                      {downloadStatus}
                    </Text>
                  </View>
                ) : null}

                {/* Action Buttons */}
                <XStack gap="$3" flexWrap="wrap">
                  <Button
                    flex={1}
                    backgroundColor="#5ac0ea"
                    color="white"
                    borderRadius={12}
                    padding="$4"
                    fontWeight="600"
                    minHeight={44}
                    pressStyle={{ backgroundColor: '#4aa3d3' }}
                    onPress={shareLink}
                    icon={<AntDesign name="share-alt" size={22} color="white" />}
                    style={{ minHeight: 44 }}
                  >
                    <SizableText color="white" fontWeight="600" minHeight={20} lineHeight={20}>
                      Share Link
                    </SizableText>
                  </Button>

                  <Button
                    flex={1}
                    backgroundColor={theme ? '#f1f5f9' : '#4b5563'}
                    color={theme ? '#374151' : '#f9fafb'}
                    borderWidth={1}
                    borderColor={theme ? '#e2e8f0' : '#6b7280'}
                    borderRadius={12}
                    padding="$4"
                    fontWeight="600"
                    minHeight={44}
                    pressStyle={{
                      backgroundColor: theme ? '#e2e8f0' : '#6b7280'
                    }}
                    onPress={copyToClipboard}
                    icon={<Feather name="copy" size={22} color={theme ? '#374151' : '#f9fafb'} />}
                    style={{ minHeight: 44 }}
                  >
                    <SizableText color={theme ? '#374151' : '#f9fafb'} fontWeight="600" minHeight={20} lineHeight={20}>
                      Copy Link
                    </SizableText>
                  </Button>

                  <Button
                    flex={1}
                    backgroundColor="#28a745"
                    color="white"
                    borderRadius={12}
                    padding="$4"
                    fontWeight="600"
                    minHeight={44}
                    pressStyle={{ backgroundColor: '#218838' }}
                    onPress={downloadQRCode}
                    disabled={downloadStatus.includes('Generating')}
                    icon={<Feather name="download" size={22} color="white" />}
                    style={{ minHeight: 44 }}
                  >
                    <SizableText color="white" fontWeight="600" minHeight={20} lineHeight={20}>
                      {downloadStatus.includes('Generating') ? 'Generating...' : 'Download QR'}
                    </SizableText>
                  </Button>
                </XStack>

                {/* Embed Code Section */}
                <View style={{
                  backgroundColor: theme ? '#f8fafc' : '#374151',
                  borderRadius: 16,
                  padding: 20,
                  borderWidth: 1,
                  borderColor: theme ? '#e5e7eb' : '#4b5563',
                }}>
                  <Button
                    backgroundColor="transparent"
                    color={theme ? '#374151' : '#f9fafb'}
                    borderWidth={1}
                    borderColor={theme ? '#e2e8f0' : '#6b7280'}
                    borderRadius={12}
                    padding="$3"
                    fontWeight="500"
                    minHeight={44}
                    pressStyle={{ backgroundColor: theme ? '#e2e8f0' : '#6b7280' }}
                    onPress={() => setShowEmbedOptions(!showEmbedOptions)}
                    icon={<Feather name="code" size={20} color={theme ? '#374151' : '#f9fafb'} />}
                    style={{ marginBottom: showEmbedOptions ? 16 : 0, minHeight: 44 }}
                  >
                    <SizableText color={theme ? '#374151' : '#f9fafb'} fontWeight="500" minHeight={20} lineHeight={20}>
                      {showEmbedOptions ? 'Hide Embed Options' : 'Show Embed Code'}
                    </SizableText>
                  </Button>

                  {showEmbedOptions && (
                    <YStack gap={16}>
                      {/* Dimension Controls */}
                      <XStack gap={12} alignItems="center">
                        <View style={{ flex: 1 }}>
                          <Text style={[stylesLocal.labelText, { color: theme ? '#374151' : '#f9fafb' }]}>
                            Width
                          </Text>
                          <TextInput
                            style={[stylesLocal.dimensionInput, {
                              backgroundColor: theme ? '#ffffff' : '#1f2937',
                              borderColor: theme ? '#e5e7eb' : '#4b5563',
                              color: theme ? '#374151' : '#f9fafb'
                            }]}
                            value={embedWidth}
                            onChangeText={(value) => updateEmbedDimensions(value, embedHeight)}
                            placeholder="100%"
                            placeholderTextColor={theme ? '#9ca3af' : '#6b7280'}
                          />
                        </View>
                        <View style={{ flex: 1 }}>
                          <Text style={[stylesLocal.labelText, { color: theme ? '#374151' : '#f9fafb' }]}>
                            Height (px)
                          </Text>
                          <TextInput
                            style={[stylesLocal.dimensionInput, {
                              backgroundColor: theme ? '#ffffff' : '#1f2937',
                              borderColor: theme ? '#e5e7eb' : '#4b5563',
                              color: theme ? '#374151' : '#f9fafb'
                            }]}
                            value={embedHeight}
                            onChangeText={(value) => updateEmbedDimensions(embedWidth, value)}
                            placeholder="600"
                            placeholderTextColor={theme ? '#9ca3af' : '#6b7280'}
                            keyboardType="numeric"
                          />
                        </View>
                      </XStack>

                      {/* Embed Code Display */}
                      <View>
                        <Text style={[stylesLocal.labelText, { color: theme ? '#374151' : '#f9fafb', marginBottom: 8 }]}>
                          Embed Code
                        </Text>
                        <View style={[stylesLocal.codeContainer, {
                          backgroundColor: theme ? '#1f2937' : '#0f172a',
                          borderColor: theme ? '#e5e7eb' : '#4b5563'
                        }]}>
                          <Text style={stylesLocal.codeText}>
                            {embedCode}
                          </Text>
                        </View>
                      </View>

                      {/* Copy Embed Code Button */}
                      <Button
                        backgroundColor="#6c5ce7"
                        color="white"
                        borderRadius={12}
                        padding="$4"
                        fontWeight="600"
                        minHeight={44}
                        pressStyle={{ backgroundColor: "#5b4cdb" }}
                        onPress={copyEmbedCode}
                        icon={<Feather name="copy" size={22} color="white" />}
                        style={{ minHeight: 44 }}
                      >
                        <SizableText color="white" fontWeight="600" minHeight={20} lineHeight={20}>
                          Copy Embed Code
                        </SizableText>
                      </Button>
                    </YStack>
                  )}
                </View>
              </YStack>
            </View>
          </ScrollView>

          {/* Footer Actions */}
          <View style={{
            backgroundColor: theme ? '#f8fafc' : '#374151',
            paddingHorizontal: 24,
            paddingVertical: 16,
            borderTopWidth: 1,
            borderTopColor: theme ? '#e5e7eb' : '#4b5563',
          }}>
            <XStack gap="$3" justifyContent="flex-end">
              <Dialog.Close asChild>
                <Pressable
                  onPress={() => setIsVisible(false)}
                  style={{
                    paddingHorizontal: 20,
                    paddingVertical: 12,
                    borderRadius: 12,
                    backgroundColor: theme ? '#f1f5f9' : '#4b5563',
                    borderWidth: 1,
                    borderColor: theme ? '#e2e8f0' : '#6b7280',
                    minWidth: 100,
                    minHeight: 44,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Text style={{
                    color: theme ? '#64748b' : '#d1d5db',
                    fontWeight: '600',
                    fontSize: 14,
                    minHeight: 20,
                  }}>
                    Close
                  </Text>
                </Pressable>
              </Dialog.Close>
            </XStack>
          </View>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  )
});

const stylesLocal = StyleSheet.create({
  iconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#e8f4fd',
    justifyContent: 'center',
    alignItems: 'center',
    ...Platform.select({
      ios: {
        shadowColor: '#5ac0ea',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.15,
        shadowRadius: 12,
      },
      android: {
        elevation: 6,
      },
      web: {
        boxShadow: '0 4px 20px rgba(90, 192, 234, 0.15)',
      }
    }),
  },
  qrContainer: {
    borderRadius: 16,
    padding: 20,
    alignItems: 'center',
    marginVertical: 16,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.08,
        shadowRadius: 8,
      },
      android: {
        elevation: 3,
      },
      web: {
        boxShadow: '0 2px 16px rgba(0, 0, 0, 0.08)',
      }
    }),
  },
  qrCodeWrapper: {
    padding: 16,
    backgroundColor: 'white',
    borderRadius: 12,
    marginBottom: 16,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 8,
      },
      android: {
        elevation: 4,
      },
      web: {
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.1)',
      }
    }),
  },
  linkPreview: {
    width: '100%',
    paddingVertical: 12,
  },
  statusContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    marginTop: 16,
  },
  infoSection: {
    marginTop: 24,
    padding: 16,
    backgroundColor: 'rgba(90, 192, 234, 0.05)',
    borderRadius: 12,
    borderLeftWidth: 4,
    borderLeftColor: '#5ac0ea',
  },
  infoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 4,
  },
  infoText: {
    marginLeft: 12,
    fontSize: 14,
    color: '#6c757d',
    lineHeight: 20,
    flex: 1,
  },
  embedSection: {
    marginTop: 24,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(90, 192, 234, 0.2)',
  },
  labelText: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 6,
  },
  dimensionInput: {
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    fontWeight: '500',
  },
  codeContainer: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    maxHeight: 120,
    overflow: 'scroll' as any,
  },
  codeText: {
    fontFamily: Platform.OS === 'web' ? 'Consolas, Monaco, "Courier New", monospace' : 'System',
    fontSize: 12,
    lineHeight: 18,
    color: '#00d4ff',
  },
});

export default MeetingShareModal;
